import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { 
  Search, Dumbbell, Play, X, ChevronRight, Filter, Zap, Target, 
  ArrowRight, Flame, BookOpen, Video, ListChecks
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Paywall from "@/components/Paywall";

const CAT_LABELS = { 
  alle: "Alle", bryst: "Bryst", ryg: "Ryg", skuldre: "Skuldre", 
  arme: "Arme", ben: "Ben", mave: "Mave", helkrop: "Helkrop", 
  cardio: "Cardio", mobilitet: "Mobilitet" 
};

const CAT_ICONS = {
  bryst: "💪", ryg: "🔙", skuldre: "🎯", arme: "💪",
  ben: "🦵", mave: "🔥", helkrop: "⚡", cardio: "❤️", mobilitet: "🧘"
};

const DIFF_COLORS = { 
  beginner: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", 
  intermediate: "bg-amber-500/10 text-amber-400 border-amber-500/20", 
  advanced: "bg-red-500/10 text-red-400 border-red-500/20" 
};

const DIFF_LABELS = { beginner: "Begynder", intermediate: "Middel", advanced: "Avanceret" };

export default function ExerciseLibrary() {
  const [exercises, setExercises] = useState([]);
  const [categories, setCategories] = useState(["alle"]);
  const [activeCategory, setActiveCategory] = useState("alle");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    axios.get(`${API}/exercises/categories`).then(res => setCategories(["alle", ...res.data])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeCategory !== "alle") params.category = activeCategory;
    if (search) params.search = search;
    axios.get(`${API}/exercises`, { params })
      .then(res => setExercises(res.data))
      .finally(() => setLoading(false));
  }, [activeCategory, search]);

  // Stats
  const totalExercises = exercises.length;
  const videoCount = exercises.filter(e => e.video_url).length;
  const categoryCount = categories.length - 1;

  return (
    <Paywall>
      <div className="min-h-screen bg-[#09090b]" data-testid="exercises-page">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/10 via-transparent to-transparent" />
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute top-40 right-1/4 w-64 h-64 bg-[#DFFF00]/5 rounded-full blur-3xl" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 border border-cyan-500/20 flex items-center justify-center">
                  <BookOpen className="h-7 w-7 text-cyan-400" />
                </div>
                <div>
                  <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl sm:text-4xl uppercase tracking-tight text-white">
                    Øvelses<span className="text-cyan-400">bibliotek</span>
                  </h1>
                  <p className="text-sm text-zinc-400 mt-0.5">
                    {totalExercises} øvelser • {videoCount} med video
                  </p>
                </div>
              </div>

              {/* Quick stats */}
              <div className="flex gap-3">
                <div className="bg-[#18181b] border border-white/5 rounded-xl px-4 py-3 text-center">
                  <p style={{ fontFamily: 'JetBrains Mono' }} className="text-xl font-bold text-cyan-400">{totalExercises}</p>
                  <p className="text-[10px] text-zinc-500 uppercase">Øvelser</p>
                </div>
                <div className="bg-[#18181b] border border-white/5 rounded-xl px-4 py-3 text-center">
                  <p style={{ fontFamily: 'JetBrains Mono' }} className="text-xl font-bold text-red-400">{videoCount}</p>
                  <p className="text-[10px] text-zinc-500 uppercase">Med Video</p>
                </div>
                <div className="bg-[#18181b] border border-white/5 rounded-xl px-4 py-3 text-center">
                  <p style={{ fontFamily: 'JetBrains Mono' }} className="text-xl font-bold text-[#DFFF00]">{categoryCount}</p>
                  <p className="text-[10px] text-zinc-500 uppercase">Kategorier</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          {/* Search & Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Søg efter øvelse, muskelgruppe..."
                className="bg-[#18181b] border-white/5 focus:border-cyan-500 rounded-xl h-12 pl-12 text-base" data-testid="exercise-search" />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)}
              className={`border-white/10 rounded-xl h-12 px-5 ${showFilters ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400' : 'text-zinc-400'}`}>
              <Filter className="mr-2 h-4 w-4" /> Filtre
            </Button>
          </div>

          {/* Category Pills */}
          <div className={`mb-8 transition-all ${showFilters ? 'opacity-100' : 'opacity-100'}`}>
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button key={cat} onClick={() => setActiveCategory(cat)} data-testid={`category-${cat}`}
                  className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider rounded-xl transition-all flex items-center gap-2 ${
                    activeCategory === cat 
                      ? "bg-gradient-to-r from-cyan-500 to-cyan-600 text-white shadow-lg shadow-cyan-500/20" 
                      : "bg-[#18181b] text-zinc-400 hover:text-white border border-white/5 hover:border-cyan-500/30"
                  }`}>
                  {cat !== 'alle' && <span className="text-sm">{CAT_ICONS[cat] || '📋'}</span>}
                  {CAT_LABELS[cat] || cat}
                  {activeCategory === cat && cat !== 'alle' && (
                    <span className="bg-white/20 px-1.5 py-0.5 rounded text-[10px]">
                      {exercises.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Results info */}
          {search && (
            <div className="mb-6 flex items-center gap-2 text-sm text-zinc-400">
              <span>Viser {exercises.length} resultater for</span>
              <span className="bg-cyan-500/10 text-cyan-400 px-2 py-0.5 rounded-lg">"{search}"</span>
            </div>
          )}

          {/* Exercise Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="bg-[#18181b] border border-white/5 rounded-2xl h-48 animate-pulse" />
              ))}
            </div>
          ) : exercises.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 border border-cyan-500/20 flex items-center justify-center">
                <Search className="h-10 w-10 text-cyan-400/50" />
              </div>
              <h2 style={{ fontFamily: 'Oswald' }} className="text-xl font-bold uppercase text-white mb-2">
                Ingen øvelser fundet
              </h2>
              <p className="text-zinc-400 mb-6">Prøv at søge efter noget andet eller vælg en anden kategori</p>
              <Button onClick={() => { setSearch(""); setActiveCategory("alle"); }}
                className="bg-cyan-500 text-white hover:bg-cyan-600 rounded-xl">
                Nulstil filtre
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {exercises.map(ex => (
                <button key={ex.id} onClick={() => setSelected(ex)} data-testid={`exercise-card-${ex.id}`}
                  className="bg-[#18181b] border border-white/5 rounded-2xl p-5 hover:border-cyan-500/30 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-cyan-500/5 text-left group w-full">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Dumbbell className="h-5 w-5 text-cyan-400" />
                    </div>
                    <div className="flex items-center gap-2">
                      {ex.video_url && (
                        <span className="flex items-center gap-1 bg-red-500/10 text-red-400 px-2 py-1 rounded-lg text-[10px] font-bold">
                          <Video className="h-3 w-3" /> VIDEO
                        </span>
                      )}
                      <Badge variant="outline" className={`text-[10px] font-bold rounded-lg ${DIFF_COLORS[ex.difficulty]}`}>
                        {DIFF_LABELS[ex.difficulty]}
                      </Badge>
                    </div>
                  </div>

                  {/* Title & Description */}
                  <h3 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase tracking-wide text-white mb-2 group-hover:text-cyan-400 transition-colors">
                    {ex.name}
                  </h3>
                  <p className="text-xs text-zinc-500 mb-4 line-clamp-2">{ex.description}</p>

                  {/* Muscle tags */}
                  <div className="flex flex-wrap gap-1.5">
                    {ex.muscle_groups?.slice(0, 3).map(mg => (
                      <span key={mg} className="text-[10px] bg-white/5 text-zinc-400 px-2.5 py-1 rounded-lg border border-white/5">
                        {mg}
                      </span>
                    ))}
                    {ex.muscle_groups?.length > 3 && (
                      <span className="text-[10px] text-zinc-600 px-2 py-1">+{ex.muscle_groups.length - 3}</span>
                    )}
                  </div>

                  {/* Hover indicator */}
                  <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-xs text-cyan-400">Se detaljer</span>
                    <ArrowRight className="h-4 w-4 text-cyan-400" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Exercise Detail Dialog */}
        <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
          <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-lg rounded-2xl" data-testid="exercise-detail-dialog" aria-describedby="exercise-detail-desc">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 flex items-center justify-center">
                  <Dumbbell className="h-6 w-6 text-cyan-400" />
                </div>
                <div>
                  <DialogTitle style={{ fontFamily: 'Oswald, sans-serif' }} className="uppercase text-xl tracking-wide">
                    {selected?.name}
                  </DialogTitle>
                  <Badge variant="outline" className={`text-[10px] mt-1 ${DIFF_COLORS[selected?.difficulty]}`}>
                    {DIFF_LABELS[selected?.difficulty]}
                  </Badge>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-5 mt-2">
              {/* Description */}
              <p className="text-sm text-zinc-400 leading-relaxed">{selected?.description}</p>

              {/* Muscle Groups */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-4 w-4 text-cyan-400" />
                  <span className="text-xs uppercase tracking-wider text-zinc-500 font-bold">Muskelgrupper</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selected?.muscle_groups?.map(mg => (
                    <Badge key={mg} className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20 text-xs rounded-lg">
                      {mg}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Equipment */}
              {selected?.equipment?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Dumbbell className="h-4 w-4 text-amber-400" />
                    <span className="text-xs uppercase tracking-wider text-zinc-500 font-bold">Udstyr</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {selected?.equipment?.map(eq => (
                      <Badge key={eq} variant="outline" className="border-amber-500/20 text-amber-400 text-xs rounded-lg">
                        {eq}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Instructions */}
              {selected?.instructions?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <ListChecks className="h-4 w-4 text-emerald-400" />
                    <span className="text-xs uppercase tracking-wider text-zinc-500 font-bold">Instruktioner</span>
                  </div>
                  <ol className="space-y-2">
                    {selected?.instructions?.map((step, i) => (
                      <li key={i} className="flex gap-3 text-sm bg-zinc-900/50 rounded-xl p-3 border border-white/5">
                        <span style={{ fontFamily: 'JetBrains Mono, monospace' }} 
                          className="w-6 h-6 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs flex items-center justify-center flex-shrink-0 font-bold">
                          {i+1}
                        </span>
                        <span className="text-zinc-300">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Video Button */}
              {selected?.video_url && (
                <button onClick={() => window.open(selected.video_url, '_blank', 'noopener,noreferrer')} data-testid="exercise-video-link"
                  className="flex items-center gap-3 bg-gradient-to-r from-red-500/10 to-red-500/5 border border-red-500/20 rounded-xl px-4 py-4 text-red-400 hover:bg-red-500/20 hover:border-red-500/30 transition-all w-full text-left group">
                  <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="h-5 w-5 fill-current" />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-bold block">Se video tutorial</span>
                    <span className="text-xs text-red-400/60 block">Åbner YouTube i ny fane</span>
                  </div>
                  <ChevronRight className="h-5 w-5 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Paywall>
  );
}
